<template>
    <div>
      <b-field grouped>
        <b-input expanded v-model="filters.name" placeholder="Company or Faciltiy Name" />
        <b-select v-model="filters.state" placeholder="State">
          <option></option>
          <option
            v-for="(abbreviation, longName) in usStates"
            :value="abbreviation"
            :key="abbreviation">
            {{longName }}
          </option>
        </b-select>
        <b-input expanded v-model="filters.city" placeholder="City" />
        <b-input expanded v-model="filters.zip" placeholder="Zip Code" />
      </b-field>
      Company Name
      <letter-bar v-model="filters.name" />
      <edit-modal :show.sync="showEditModal" :location-key="editingLocationKey" @updated="search" />
      <delete-confirmation :show.sync="showDeleteConfirmation"
                           :display-name="deletingLocation.companyRolodexJobLocationName"
                           @deleteConfirmed="_deleteLocation" />

      <b-table :data="list"
               striped
               hoverable
               :loading="isLoading">

        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p v-show="!list.length && !hasFilterValues">Select a letter above or type in a company name to search your address book.</p>
              <p v-show="!list.length && !isLoading && hasFilterValues">No matching locations were found.</p>
            </div>
          </section>
        </template>

        <template slot-scope="props">
          <b-table-column class="actions-cell">
            <a href="" @click.prevent="shipHere(props.row)" class="button">Select Location</a>
            <a href="" @click.prevent="edit(props.row)" class="button is-info" v-if="canEditAndDelete">
              <font-awesome-icon icon="edit" />
            </a>
            <a href="" @click.prevent="deleteLocationPrompt(props.row)" class="button is-danger" v-if="canEditAndDelete">
              <font-awesome-icon icon="trash-alt" />
            </a>
          </b-table-column>
          <b-table-column field="companyRolodexJobLocationName" label="Company/Facility Name">
            {{ props.row.companyRolodexJobLocationName}}
          </b-table-column>
          <b-table-column field="companyRolodexJobLocationAddressStreet1" label="Street Address">
            {{ props.row.companyRolodexJobLocationAddressStreet1}}
            <br/>
            {{ props.row.companyRolodexJobLocationAddressStreet2}}
          </b-table-column>
          <b-table-column field="companyRolodexJobLocationAddressState" label="State">
            {{ props.row.companyRolodexJobLocationAddressState}}
          </b-table-column>
          <b-table-column field="companyRolodexJobLocationAddressCity" label="City">
            {{ props.row.companyRolodexJobLocationAddressCity}}
          </b-table-column>
          <b-table-column field="companyRolodexJobLocationAddressZip" label="Zip Code">
            {{ props.row.companyRolodexJobLocationAddressZip}}
          </b-table-column>
        </template>
      </b-table>
    </div>
</template>

<script>
const DEBOUNCE_TIME = 300
const DEBOUNCE_OPTIONS = {
  leading: true
}

import RolodexApi from '@/api-interface/rolordex'
import { debounce } from 'lodash-es'
import hasEmptyValues from '@/helpers/hasEmptyValues'
import US_STATES from '@/lookup-data/us-states'
import LetterBar from '@/components/LetterBar'
import EditModal from '@/components/RolodexLocationEdit'
import DeleteConfirmation from '@/components/DeleteConfirmation'

export default {
  name: 'RolodexLocationList',

  data() {
    return {
      showEditModal: false,
      showDeleteConfirmation: false,
      editingLocationKey: '',
      deletingLocation: '',
      usStates: US_STATES,
      isLoading: false,
      filters: {
        name: '',
        city: '',
        state: '',
        zip: ''
      },
      rawList: []
    }
  },

  mounted() {
    this.search()
  },

  computed: {
    hasFilterValues() {
      return !hasEmptyValues(this.filters)
    },

    list() {
      return this.rawList.map((location) => {
        // flatten the "addresses" Array since each entry will only have 1 address
        return {
          ...location,
          ...location.companyRolodexJobLocationAddresses[0]
        }
      })
    }
  },

  props: {
    canEditAndDelete: {
      type: Boolean,
      default: true
    }
  },

  components: {
    LetterBar,
    EditModal,
    DeleteConfirmation
  },

  methods: {
    shipHere(companyRolodexJobLocation) {
      this.$emit('selected', companyRolodexJobLocation)
    },

    toggleEditModal() {
      this.showEditModal = !this.showEditModal
    },

    toggleDeleteConfirmation() {
      this.showDeleteConfirmation = !this.showDeleteConfirmation
    },

    edit(location) {
      this.editingLocationKey = location.companyRolodexJobLocationKey
      this.toggleEditModal()
    },

    deleteLocationPrompt(location) {
      this.deletingLocation = location
      this.toggleDeleteConfirmation()
    },

    async _deleteLocation() {
      try {
        await RolodexApi.deleteLocationByKey(this.deletingLocation.companyRolodexJobLocationKey)
        this.search()
      } catch (error) {
        window.alert('We were not able to delete this location because it is part of an active transaction')
      }
    },

    search: debounce(
      async function() {
        this.isLoading = true
        try {
          if (this.hasFilterValues) {
            this.rawList = await RolodexApi.locationSearch(this.filters)
          } else {
            // if no filter values are present, perform a wild-card search
            this.rawList = await RolodexApi.locationSearch({ name: '%' })
          }
        } catch (error) {
          console.error(`Error searching for [${this.filters}]: `, error)
        } finally {
          this.isLoading = false
        }
      },
      DEBOUNCE_TIME,
      DEBOUNCE_OPTIONS
    )
  },

  watch: {
    filters: {
      deep: true,
      handler: 'search'
    }
  }
}
</script>

<style scoped lang="scss">
.actions-cell {
  .button {
    margin: 0 3px;
  }
}
</style>
